import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageCircle, X } from 'lucide-react';
import api from '../../lib/api';

/**
 * Cross-module floating notification for new WhatsApp messages — visible on
 * any Elios B2B page, not just /elios/whatsapp. Per-user preference (backend
 * `GET/PUT /elios-client/whatsapp/bolle/stato`), off by default when the
 * WhatsApp module isn't enabled for this client (the endpoint 403s, caught
 * silently below, so the bubble just never renders in that case).
 */
export default function WhatsAppBubble() {
  const [state, setState] = useState({ attive: false, non_letti: 0 });
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const refresh = useCallback(() => {
    api.get('/elios-client/whatsapp/bolle/stato')
      .then(r => setState(r.data))
      .catch(() => setState({ attive: false, non_letti: 0 }));
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(() => { if (!document.hidden) refresh(); }, 20000);
    return () => clearInterval(id);
  }, [refresh]);

  // Already on the WhatsApp page, or user closed it, or turned off, or
  // nothing unread — nothing to show.
  if (!state.attive || state.non_letti === 0 || dismissed || location.pathname.startsWith('/elios/whatsapp')) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => navigate('/elios/whatsapp')}
      className="fixed bottom-20 lg:bottom-6 right-4 sm:right-6 z-40 flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-green-500 hover:bg-green-600 text-white shadow-lg transition-all hover:scale-[1.03] active:scale-95"
      data-testid="wa-bubble"
    >
      <span className="relative">
        <MessageCircle className="w-5 h-5" />
        <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-white text-green-600 text-[9px] font-bold flex items-center justify-center">
          {state.non_letti > 9 ? '9+' : state.non_letti}
        </span>
      </span>
      <span className="text-xs font-medium hidden sm:inline">
        {state.non_letti} nuov{state.non_letti === 1 ? 'o' : 'i'} su WhatsApp
      </span>
      <span
        role="button"
        onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
        className="ml-1 p-0.5 rounded-full hover:bg-white/20"
        title="Nascondi"
        data-testid="wa-bubble-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </span>
    </button>
  );
}
